/* ==========================================================================
   Trufairs Wishlist — page controller
   --------------------------------------------------------------------------
   Loaded only by sections/main-wishlist.liquid.

   Renders every locally saved product as one table row using LIVE Shopify
   data: each row is fetched as /products/<handle>?view=wishlist-card, which
   returns the row markup with no layout around it. Prices, variant titles and
   availability are therefore always current, and nothing is ever read back
   from the price that was in localStorage.

   The row owns four controls:
     - variant <select>   (only when the product has more than one variant)
     - quantity input     with -/+ steppers
     - Add to cart        posts to /cart/add.js with the chosen variant AND
                          the chosen quantity, then hands off to the theme's
                          cart drawer through the same events assets/add-to-cart.js
                          dispatches
     - Remove             deletes the entry from the wishlist
   ========================================================================== */

(function () {
  'use strict';

  // 8 rather than 4: these are same-origin HTTP/2 requests to a host the
  // browser is already connected to, so the old limit was leaving the
  // connection idle rather than protecting it.
  var CONCURRENCY = 8;
  var MAX_QTY = 999;

  // Rows above the fold are fetched with priority and load their image
  // eagerly; everything below keeps loading="lazy".
  var EAGER_ROWS = 3;

  var SELECTORS = {
    root: '[data-wishlist-page]',
    grid: '[data-wishlist-grid]',
    table: '[data-wishlist-table]',
    empty: '[data-wishlist-empty]',
    footer: '[data-wishlist-footer]',
    skeleton: '[data-wishlist-skeleton]',
    countLabel: '[data-wishlist-page-count]',
    row: '[data-wishlist-row]',
    removeBtn: '[data-wishlist-remove]',
    addBtn: '[data-wishlist-add]',
    addLabel: '[data-wishlist-add-label]',
    qtyInput: '[data-wishlist-qty]',
    qtyStep: '[data-wishlist-qty-step]',
    variantSelect: '[data-wishlist-variant-select]',
    variantData: '[data-wishlist-variants]',
    price: '[data-wishlist-price]',
    compare: '[data-wishlist-compare]',
    image: '[data-wishlist-image]'
  };

  var STRINGS = {
    add: 'Add to cart',
    adding: 'Adding…',
    added: 'Added!',
    soldOut: 'Sold out',
    error: 'Try again'
  };

  function toast(message) {
    if (window.TrufairsWishlist && typeof window.TrufairsWishlist.toast === 'function') {
      window.TrufairsWishlist.toast(message);
      return;
    }
    // The core module owns the toast region; without it a message is simply
    // not shown. Cart state is never affected either way.
  }

  function WishlistPage(root) {
    this.root = root;
    this.grid = root.querySelector(SELECTORS.grid);
    this.table = root.querySelector(SELECTORS.table);
    this.empty = root.querySelector(SELECTORS.empty);
    this.footer = root.querySelector(SELECTORS.footer);
    this.skeleton = root.querySelector(SELECTORS.skeleton);
    this.countLabel = root.querySelector(SELECTORS.countLabel);
    this.rendering = false;
    this.renderToken = 0;
    this.painted = 0;

    this.onWishlistChange = this.onWishlistChange.bind(this);
    this.onClick = this.onClick.bind(this);
    this.onChange = this.onChange.bind(this);

    document.addEventListener('wishlist:change', this.onWishlistChange);
    this.root.addEventListener('click', this.onClick);
    this.root.addEventListener('change', this.onChange);

    this.render();
  }

  WishlistPage.prototype.api = function () {
    return window.TrufairsWishlist || null;
  };

  WishlistPage.prototype.onWishlistChange = function (evt) {
    var detail = evt && evt.detail;

    // A removal triggered from this page just detaches the row, so the table
    // is not rebuilt and no other row is refetched.
    if (detail && (detail.action === 'remove' || detail.action === 'prune') && detail.productId) {
      var row = this.grid.querySelector('[data-wishlist-item="' + detail.productId + '"]');
      if (row) {
        row.parentNode.removeChild(row);
        this.updateCount();
        this.updateEmptyState();
        return;
      }
    }

    if (detail && detail.action === 'add') {
      // Only reachable from another tab.
      this.render();
      return;
    }

    this.updateCount();
    this.updateEmptyState();
  };

  /* ------------------------------------------------------------------
     Interaction
     ------------------------------------------------------------------ */

  WishlistPage.prototype.onClick = function (evt) {
    var target = evt.target;
    if (!target || !target.closest) return;

    var removeBtn = target.closest(SELECTORS.removeBtn);
    if (removeBtn && this.root.contains(removeBtn)) {
      evt.preventDefault();
      var api = this.api();
      if (api) api.remove(removeBtn.getAttribute('data-product-id'));
      return;
    }

    var stepBtn = target.closest(SELECTORS.qtyStep);
    if (stepBtn && this.root.contains(stepBtn)) {
      evt.preventDefault();
      this.step(stepBtn, parseInt(stepBtn.getAttribute('data-wishlist-qty-step'), 10) || 0);
      return;
    }

    var addBtn = target.closest(SELECTORS.addBtn);
    if (addBtn && this.root.contains(addBtn)) {
      evt.preventDefault();
      this.addToCart(addBtn);
    }
  };

  WishlistPage.prototype.onChange = function (evt) {
    var target = evt.target;
    if (!target || !target.closest) return;

    if (target.matches(SELECTORS.variantSelect)) {
      this.applyVariant(target.closest(SELECTORS.row), target.value);
      return;
    }

    if (target.matches(SELECTORS.qtyInput)) {
      target.value = this.readQty(target);
    }
  };

  /**
   * Read a quantity input as a sane integer. Anything non-numeric, zero,
   * negative or absurd collapses to a valid value rather than being posted
   * to /cart/add.js, which would 422 on it.
   */
  WishlistPage.prototype.readQty = function (input) {
    if (!input) return 1;
    var value = parseInt(input.value, 10);
    if (!isFinite(value) || value < 1) value = 1;
    if (value > MAX_QTY) value = MAX_QTY;
    return value;
  };

  WishlistPage.prototype.step = function (btn, delta) {
    var row = btn.closest(SELECTORS.row);
    if (!row) return;
    var input = row.querySelector(SELECTORS.qtyInput);
    if (!input) return;
    var next = this.readQty(input) + delta;
    if (next < 1) next = 1;
    if (next > MAX_QTY) next = MAX_QTY;
    input.value = next;
  };

  /**
   * Repaint a row for the newly selected variant: price, compare-at price,
   * thumbnail and the Add to cart button's target and disabled state.
   * The data comes from the JSON blob Liquid emitted with the row, so money
   * formatting is never reimplemented here.
   */
  WishlistPage.prototype.applyVariant = function (row, variantId) {
    if (!row || !variantId) return;

    var variants = this.variantsFor(row);
    var variant = null;
    for (var i = 0; i < variants.length; i++) {
      if (String(variants[i].id) === String(variantId)) {
        variant = variants[i];
        break;
      }
    }
    if (!variant) return;

    var price = row.querySelector(SELECTORS.price);
    if (price) price.textContent = variant.price;

    // Keep the row's machine-readable price and variant in step with the
    // visible ones — assets/wishlist-ga4.js reads them off the row.
    row.setAttribute('data-variant-id', variant.id);
    if (variant.priceCents != null) {
      row.setAttribute('data-product-price', variant.priceCents);
    }

    var compare = row.querySelector(SELECTORS.compare);
    if (compare) {
      if (variant.compareAtPrice) {
        compare.textContent = variant.compareAtPrice;
        compare.hidden = false;
      } else {
        compare.textContent = '';
        compare.hidden = true;
      }
    }

    var img = row.querySelector(SELECTORS.image);
    if (img && variant.image) {
      img.src = variant.image;
      if (variant.image2x) img.srcset = variant.image + ' 1x, ' + variant.image2x + ' 2x';
    }

    var addBtn = row.querySelector(SELECTORS.addBtn);
    if (addBtn) {
      addBtn.setAttribute('data-variant-id', variant.id);
      this.setAddState(addBtn, variant.available ? 'idle' : 'soldout');
    }
  };

  WishlistPage.prototype.variantsFor = function (row) {
    if (row.__wishlistVariants) return row.__wishlistVariants;
    var node = row.querySelector(SELECTORS.variantData);
    var parsed = [];
    if (node) {
      try {
        parsed = JSON.parse(node.textContent) || [];
      } catch (e) {
        parsed = [];
      }
    }
    row.__wishlistVariants = parsed;
    return parsed;
  };

  /**
   * The Add / Sold out wording is emitted by Liquid on the button itself, so
   * a translated storefront keeps its own copy instead of the English
   * fallbacks in STRINGS.
   */
  function labelFor(btn, key) {
    return btn.getAttribute('data-label-' + key) || (key === 'add' ? STRINGS.add : STRINGS.soldOut);
  }

  WishlistPage.prototype.setAddState = function (btn, state) {
    var label = btn.querySelector(SELECTORS.addLabel) || btn;

    if (state === 'soldout') {
      btn.disabled = true;
      btn.setAttribute('aria-disabled', 'true');
      btn.classList.remove('is-adding', 'is-added');
      label.textContent = labelFor(btn, 'soldout');
      return;
    }

    if (state === 'busy') {
      btn.disabled = true;
      btn.setAttribute('aria-disabled', 'true');
      btn.classList.add('is-adding');
      label.textContent = STRINGS.adding;
      return;
    }

    btn.classList.remove('is-adding');
    btn.disabled = false;
    btn.removeAttribute('aria-disabled');

    if (state === 'done') {
      btn.classList.add('is-added');
      label.textContent = STRINGS.added;
      return;
    }

    if (state === 'error') {
      label.textContent = STRINGS.error;
      return;
    }

    btn.classList.remove('is-added');
    label.textContent = labelFor(btn, 'add');
  };

  /**
   * Add one row to the cart with the quantity the customer typed.
   *
   * The theme's assets/add-to-cart.js is not reused: it is hard-coded to
   * quantity 1 and binds itself to `.product-card-add-to-cart`. The events
   * dispatched below are the exact set that file dispatches, so the cart
   * drawer, the header count and any app listening for them behave
   * identically to an add from a product card.
   */
  WishlistPage.prototype.addToCart = function (btn) {
    if (btn.disabled || btn.classList.contains('is-adding')) return;

    var row = btn.closest(SELECTORS.row);
    var variantId = btn.getAttribute('data-variant-id');
    if (!variantId) return;

    var qtyInput = row ? row.querySelector(SELECTORS.qtyInput) : null;
    var quantity = this.readQty(qtyInput);
    if (qtyInput) qtyInput.value = quantity;

    var self = this;
    var cartAddUrl =
      (window.TrufairsWishlistConfig &&
        window.TrufairsWishlistConfig.routes &&
        window.TrufairsWishlistConfig.routes.cartAdd) ||
      '/cart/add.js';

    this.setAddState(btn, 'busy');

    var body = new FormData();
    body.append('id', variantId);
    body.append('quantity', String(quantity));

    fetch(cartAddUrl, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
      body: body
    })
      .then(function (res) {
        return res.json().then(function (data) {
          if (!res.ok) {
            var err = new Error(data && data.description ? data.description : 'Add to cart failed');
            err.data = data;
            throw err;
          }
          return data;
        });
      })
      .then(function (data) {
        self.setAddState(btn, 'done');

        document.dispatchEvent(
          new CustomEvent('ajaxProduct:added', {
            bubbles: true,
            detail: { product: data, addToCartBtn: btn, quantity: quantity }
          })
        );

        ['cart:item-added', 'cart:updated', 'product:added-to-cart'].forEach(function (name) {
          document.dispatchEvent(
            new CustomEvent(name, {
              bubbles: true,
              detail: { product: data, variant: data, quantity: quantity, addToCartBtn: btn }
            })
          );
        });

        if (window.theme && typeof window.theme.cartCount === 'function') {
          window.theme.cartCount();
        }

        window.setTimeout(function () {
          if (
            window.theme &&
            window.theme.CartDrawer &&
            typeof window.theme.CartDrawer.open === 'function'
          ) {
            window.theme.CartDrawer.open();
          }
        }, 100);

        window.setTimeout(function () {
          // The variant may have sold out between render and add; re-read the
          // row's own data rather than assuming it is still purchasable.
          self.restoreAddState(row, btn);
        }, 1600);
      })
      .catch(function (error) {
        self.setAddState(btn, 'error');
        toast((error && error.message) || 'Could not add this item to the cart.');
        window.setTimeout(function () {
          self.restoreAddState(row, btn);
        }, 2200);
      });
  };

  WishlistPage.prototype.restoreAddState = function (row, btn) {
    if (!btn || !btn.isConnected) return;
    var available = true;
    var select = row ? row.querySelector(SELECTORS.variantSelect) : null;

    if (select) {
      var option = select.options[select.selectedIndex];
      available = !option || option.getAttribute('data-available') !== 'false';
    } else if (row) {
      available = row.getAttribute('data-product-available') !== 'false';
    }

    this.setAddState(btn, available ? 'idle' : 'soldout');
  };

  /* ------------------------------------------------------------------
     Rendering
     ------------------------------------------------------------------ */

  WishlistPage.prototype.render = function () {
    var api = this.api();
    if (!api) return;

    var token = ++this.renderToken;
    var items = api.getItems();

    this.updateCount();

    if (!items.length) {
      this.rendering = false;
      this.hideSkeleton();
      this.grid.innerHTML = '';
      this.updateEmptyState();
      return;
    }

    // Held until every fetch settles. While it is set, updateEmptyState()
    // stands down:
    // the table is legitimately empty during the load, and a stale entry being
    // pruned mid-flight would otherwise flash "Your wishlist is empty" over a
    // wishlist that does have products.
    this.rendering = true;
    this.showSkeleton();
    this.empty.hidden = true;
    if (this.table) this.table.hidden = true;
    if (this.footer) this.footer.hidden = true;

    var self = this;
    var cursor = 0;

    // One empty <tr> per saved product, inserted up front in wishlist order.
    // Each row then replaces its own slot the moment its fetch resolves, so
    // products appear progressively and always in the right position — no
    // waiting for the slowest request before anything is shown.
    var slots = new Array(items.length);
    var frag = document.createDocumentFragment();
    for (var i = 0; i < items.length; i++) {
      var slot = document.createElement('tr');
      slot.className = 'wishlist-table__row wishlist-table__row--pending';
      slot.hidden = true;
      slots[i] = slot;
      frag.appendChild(slot);
    }
    this.grid.innerHTML = '';
    this.grid.appendChild(frag);

    this.painted = 0;

    function next() {
      if (token !== self.renderToken) return Promise.resolve();
      if (cursor >= items.length) return Promise.resolve();
      var i = cursor++;
      return self
        .fetchRow(items[i], i)
        .then(function (node) {
          self.placeRow(token, slots, i, node);
        })
        .then(next);
    }

    var workers = [];
    for (var w = 0; w < Math.min(CONCURRENCY, items.length); w++) {
      workers.push(next());
    }

    Promise.all(workers).then(function () {
      if (token !== self.renderToken) return;
      self.finishRender();
    });
  };

  /**
   * Swap one resolved row into its slot. A null node means the product no
   * longer exists in Shopify, so the slot is simply dropped.
   *
   * The first row to land is what hides the skeleton and reveals the table —
   * not the last one.
   */
  WishlistPage.prototype.placeRow = function (token, slots, index, node) {
    if (token !== this.renderToken) return;

    var slot = slots[index];
    if (!slot || !slot.parentNode) return;

    if (!node) {
      slot.parentNode.removeChild(slot);
      return;
    }

    // Set while the row is still detached, so the browser never starts a
    // lazy load and then has to reprioritise it.
    if (index < EAGER_ROWS) {
      var img = node.querySelector(SELECTORS.image);
      if (img) {
        img.setAttribute('loading', 'eager');
        img.setAttribute('fetchpriority', 'high');
      }
    }

    slot.parentNode.replaceChild(node, slot);
    this.painted++;

    if (this.painted === 1) {
      this.hideSkeleton();
      if (this.table) this.table.hidden = false;
      if (this.footer) this.footer.hidden = false;
    }
  };

  /**
   * Called once every fetch has settled. Clears the rendering guard, drops
   * any slot whose request never produced a row, and announces the finished
   * list — analytics and re-hydration both want the complete set, once.
   */
  WishlistPage.prototype.finishRender = function () {
    var leftovers = this.grid.querySelectorAll('.wishlist-table__row--pending');
    for (var i = 0; i < leftovers.length; i++) {
      if (leftovers[i].parentNode) leftovers[i].parentNode.removeChild(leftovers[i]);
    }

    this.hideSkeleton();
    this.rendering = false;

    this.updateCount();
    this.updateEmptyState();

    // Lets the wishlist core re-hydrate anything it owns inside the new rows,
    // and any other theme code that listens for injected product markup.
    document.dispatchEvent(
      new CustomEvent('wishlist:page-rendered', { detail: { count: this.painted } })
    );

    if (window.AOS && typeof window.AOS.refreshHard === 'function') {
      window.AOS.refreshHard();
    }
  };

  /**
   * Parse one row of fetched markup.
   *
   * A single reused <template> rather than a new DOMParser document per row:
   * DOMParser builds a whole standalone Document each call, which is by far
   * the most expensive part of painting a long wishlist. The response already
   * wraps the row in <table><tbody>, so the parser nests the <tr> correctly.
   *
   * No importNode: inserting a node adopts it into this document anyway, and
   * importNode would deep-clone every row for nothing.
   */
  var rowParser = null;

  function parseRow(html) {
    if (!rowParser) rowParser = document.createElement('template');
    rowParser.innerHTML = html;
    var row = rowParser.content.querySelector(SELECTORS.row);
    if (row && row.parentNode) row.parentNode.removeChild(row);
    rowParser.innerHTML = '';
    return row;
  }

  /**
   * Fetch one live row. A 404 means the product was deleted or unpublished,
   * so the stale entry is dropped from localStorage without a toast.
   *
   * @param {object} item  wishlist entry
   * @param {number} index position in the list; the first rows are requested
   *   at high priority because they are the ones above the fold.
   */
  WishlistPage.prototype.fetchRow = function (item, index) {
    var api = this.api();
    if (!item.handle) {
      if (api) api.remove(item.id, { silent: true });
      return Promise.resolve(null);
    }

    var root = (window.TrufairsWishlistConfig && window.TrufairsWishlistConfig.routes.root) || '/';
    var url =
      (root + '/products/' + encodeURIComponent(item.handle)).replace(/\/{2,}/g, '/') +
      '?view=wishlist-card';

    var options = { credentials: 'same-origin' };
    // Priority Hints. Ignored by browsers that do not support it.
    if (typeof index === 'number' && index < EAGER_ROWS) options.priority = 'high';

    return fetch(url, options)
      .then(function (res) {
        if (res.status === 404 || res.status === 410) {
          if (api) api.remove(item.id, { silent: true });
          return null;
        }
        if (!res.ok) return null;
        return res.text();
      })
      .then(function (html) {
        if (!html) return null;
        var row = parseRow(html);
        if (!row) return null;

        row.setAttribute('data-wishlist-item', item.id);
        return row;
      })
      .catch(function () {
        return null;
      });
  };

  WishlistPage.prototype.showSkeleton = function () {
    if (this.skeleton) this.skeleton.hidden = false;
  };

  WishlistPage.prototype.hideSkeleton = function () {
    if (this.skeleton) this.skeleton.hidden = true;
  };

  WishlistPage.prototype.updateCount = function () {
    var api = this.api();
    if (!this.countLabel || !api) return;
    var total = api.count();
    if (total === 0) {
      this.countLabel.textContent = '';
      return;
    }
    this.countLabel.textContent = total === 1 ? '1 item saved' : total + ' items saved';
  };

  WishlistPage.prototype.updateEmptyState = function () {
    var api = this.api();
    if (!api || this.rendering) return;
    var isEmpty = api.count() === 0 || this.grid.children.length === 0;
    this.empty.hidden = !isEmpty;
    if (this.table) this.table.hidden = isEmpty;
    if (this.footer) this.footer.hidden = isEmpty;
    if (isEmpty) this.hideSkeleton();
  };

  WishlistPage.prototype.destroy = function () {
    document.removeEventListener('wishlist:change', this.onWishlistChange);
    this.root.removeEventListener('click', this.onClick);
    this.root.removeEventListener('change', this.onChange);
    this.rendering = false;
    // Invalidates any in-flight render so its results are never painted.
    this.renderToken++;
  };

  /* ------------------------------------------------------------------
     Boot + theme editor lifecycle. Instances are tracked by section id so a
     section reload replaces its instance instead of stacking listeners.
     ------------------------------------------------------------------ */

  var instances = Object.create(null);

  function mount(root) {
    if (!root) return;
    var id = root.getAttribute('data-section-id') || 'default';
    var existing = instances[id];

    // Already driving this exact node: nothing to do. Without this guard a
    // second mount would refetch every row for no reason. A genuine theme
    // editor reload replaces the node, so the identity check still detects it.
    if (existing && existing.root === root && root.isConnected !== false) return;

    if (existing) existing.destroy();
    instances[id] = new WishlistPage(root);
  }

  function mountAll(scope) {
    (scope || document).querySelectorAll(SELECTORS.root).forEach(mount);
  }

  /**
   * Shopify dispatches section lifecycle events on the section's own element,
   * so the event target is where to look. Requiring an Element (nodeType 1)
   * ignores any document-level re-dispatch an app might do, which would
   * otherwise match every wishlist section on the page.
   */
  function sectionRoot(evt) {
    var el = evt && evt.target;
    if (!el || el.nodeType !== 1) return null;
    if (el.matches && el.matches(SELECTORS.root)) return el;
    return el.querySelector ? el.querySelector(SELECTORS.root) : null;
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      mountAll();
    });
  } else {
    mountAll();
  }

  document.addEventListener('shopify:section:load', function (evt) {
    var target = sectionRoot(evt);
    if (target) mount(target);
  });

  document.addEventListener('shopify:section:unload', function (evt) {
    var target = sectionRoot(evt);
    if (!target) return;
    var id = target.getAttribute('data-section-id') || 'default';
    if (instances[id]) {
      instances[id].destroy();
      delete instances[id];
    }
  });
})();
